import { W3CTraceContextPropagator } from "@opentelemetry/core";
import { ZoneContextManager } from "@opentelemetry/context-zone";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { DocumentLoadInstrumentation } from "@opentelemetry/instrumentation-document-load";
import { FetchInstrumentation } from "@opentelemetry/instrumentation-fetch";
import { resourceFromAttributes } from "@opentelemetry/resources";
import {
  BatchSpanProcessor,
  ParentBasedSampler,
  TraceIdRatioBasedSampler,
} from "@opentelemetry/sdk-trace-base";
import { WebTracerProvider } from "@opentelemetry/sdk-trace-web";
import { ATTR_SERVICE_NAME } from "@opentelemetry/semantic-conventions";

import { getOtelConfig } from "./otelConfig";

let initialized = false;

/**
 * PGS-3499: browser-side tracer bootstrap, one per app (each of pantryshop,
 * pepsicoshop, teamzone, snacks, checkout calls this from its own
 * `instrumentation-client.ts` with its own `serviceName`, so traces group by
 * app in Jaeger/Datadog). Mirrors gatorade-storefront's `otel-browser.ts`
 * (PGS-3060) — see that file for the reasoning behind each choice below;
 * this is the same design applied to a different Apollo Client major
 * version (3.x here vs. 4.x there), which only affects `otelLink.ts`, not
 * this bootstrap.
 *
 * `FetchInstrumentation` deliberately does not set `propagateTraceHeaderCorsUrls`.
 * That would make it inject its own traceparent on cross-origin fetches — the
 * same PEP3 calls `otelLink.ts` already covers — producing two competing
 * headers on one request. `FetchInstrumentation` still creates a span for
 * every fetch call, cross-origin included, it just doesn't try to propagate
 * a header onto it.
 *
 * PGS-3553: three modes, selected by `getOtelConfig()` (see that file for
 * the env vars and defaults):
 * - Switched off (`propagationEnabled` false) — returns early, same as
 *   before this ticket: no provider, no propagator, no context manager.
 * - Propagate-only (`propagationEnabled` true, no `tracesEndpoint`) — staging
 *   and production today. Registers the provider, the real
 *   `W3CTraceContextPropagator` and `ZoneContextManager` so `otelLink.ts` can
 *   inject a `traceparent` and keep its span active for
 *   `otelSentryTags.ts`, but with no `spanProcessors` and no
 *   `registerInstrumentations` call — there's no browser-facing OTLP
 *   endpoint to export to or collect extra spans for.
 * - Full export (`tracesEndpoint` set) — identical to today: adds the
 *   `BatchSpanProcessor`/OTLP exporter and the document-load/fetch
 *   instrumentations. This is local Tilt's mode and is unchanged by this
 *   ticket.
 */
export function initOtelBrowser(serviceName: string): void {
  if (initialized) {
    return;
  }

  const { tracesEndpoint, sampleRatio, propagationEnabled } = getOtelConfig();
  if (!propagationEnabled) {
    return;
  }

  const provider = new WebTracerProvider({
    resource: resourceFromAttributes({
      [ATTR_SERVICE_NAME]: serviceName,
    }),
    sampler: new ParentBasedSampler({
      root: new TraceIdRatioBasedSampler(sampleRatio),
    }),
    ...(tracesEndpoint
      ? {
          spanProcessors: [
            new BatchSpanProcessor(
              new OTLPTraceExporter({ url: tracesEndpoint })
            ),
          ],
        }
      : {}),
  });

  provider.register({
    contextManager: new ZoneContextManager(),
    propagator: new W3CTraceContextPropagator(),
  });

  if (tracesEndpoint) {
    registerInstrumentations({
      instrumentations: [
        new DocumentLoadInstrumentation(),
        new FetchInstrumentation(),
      ],
    });
  }

  initialized = true;
}
